import { read, TABLE } from '../data/db';
import { coachCards, matches } from '../data/repos';
import { byMatchHistory } from '../domain/file';
import { ARTIFACT_PHASE } from '../types';
import type { CoachCardRow, MatchRow } from '../types';

/**
 * B spec §4.5 — **the opponent-prep read** (build unit B-7, scenario 34's moment of use): the
 * latest finalized points-of-emphasis card for a team, surfaced on match setup while preparing
 * for the match against them. The A convention, unchanged: one readonly transaction, `undefined`
 * means "there is nothing to show", `StorageError` is thrown rather than swallowed.
 *
 * ── What qualifies, spelled as the three §2.5 coordinates ────────────────────
 *
 * A *points-of-emphasis* card is the (advance scout, full time) framing — §2.5's table row —
 * so the filter is exactly: the match's `purpose` is `advanceScout`, the card's `context` is
 * `fullTime`, and the card's `status` is `final`. Each leg carries a scenario-34 negative:
 *
 * - **Drafts never surface** (§2.4 prep block): `status === 'final'` only.
 * - **A team match's full-time review never surfaces here**: it answers "how did we play?", not
 *   "how do we beat them?" — the purpose leg excludes it.
 * - **Another opponent's card never surfaces**: the read starts from `listMatchesForTeam`, so a
 *   card can only arrive through a match of *this* team.
 *
 * ── "Latest" is the most recent SCOUTING, not the most recent tap ────────────
 *
 * Ordered by `byMatchHistory` (FILE-3's newest-first, total order over match date). A card from
 * an older scouting finalized *later* than a newer scouting's card is still the older look at
 * this opponent — the match date is the fact about the opponent; `finalizedAt` is a fact about
 * when the user got around to finishing the card. `resolve-references-at-consume-time`: read at
 * setup time, never cached — a card finalized between two setups surfaces on the second.
 */

const MODULE = 'app/opponentPrep';

export interface OpponentPrep {
  /** The latest finalized advance-scout card for this team. Render read-only (§2.4 point 4). */
  readonly card: CoachCardRow;
  /** The scouting match's calendar date (`YYYY-MM-DD`) — the prep block's "from scouting" line. */
  readonly matchDate: string;
}

/**
 * Scenario 34's setup-prep half: the latest finalized points-of-emphasis card for `teamId`, or
 * `undefined` when no scouting of this opponent has ever been finalized — the ordinary state,
 * which the setup screen answers by rendering no prep block at all (never an empty one).
 *
 * The current setup being built is a `preMatch` match and every card window excludes `preMatch`
 * (B §4.2), so it can never hold a finalized card and needs no exclusion parameter here.
 *
 * @throws {StorageError} on a read failure.
 */
export async function loadOpponentPrep(teamId: string): Promise<OpponentPrep | undefined> {
  return read(
    [TABLE.matches, TABLE.coachCards],
    async (): Promise<OpponentPrep | undefined> => {
      const scouted = (await matches.listMatchesForTeam(teamId))
        .filter((row: MatchRow) => row.purpose === 'advanceScout')
        .sort(byMatchHistory);

      for (const match of scouted) {
        const card = await coachCards.getForMatchContext(match.id, ARTIFACT_PHASE.fullTime);
        if (card !== undefined && card.status === 'final') {
          return { card, matchDate: match.date };
        }
      }
      return undefined;
    },
    MODULE,
  );
}
